/**
 * Resolving "which widget do we ring" against a real subscriber's layout.
 *
 * The demo guide resolves aliases through its own template — "filtered" becomes
 * template.filteredHeadlinesId, "primary" is the literal "NewsquawkHeadlines-primary"
 * — and that works only because applyDemoTemplate writes those grid ids
 * verbatim into a tab it replaces wholesale. None of it transfers here. A
 * subscriber's tab is theirs: server-persisted, dragged around, and holding
 * composite ids minted per instance ("NewsquawkHeadlines-a8f3k2"). There is no
 * name to look up, so the only stable handle is the widget TYPE.
 *
 * Import-free (no widget registry, no store) so it stays testable under node.
 */

import type { RailTargetSpec } from "./steps";

/** The slice of DashboardTab this module needs. Structural, so no import. */
export type TabLike = {
  enabledWidgets: string[];
  widgetFilters?: Record<string, FilterDataLike | undefined>;
};

export type FilterDataLike = {
  filter?: Record<string, string[] | undefined>;
  highlight?: Record<string, string[] | undefined>;
};

/**
 * Is this grid id an instance of that base id?
 *
 * The "-" boundary matters. extractBaseId in utils/widgets.ts splits on the
 * first dash, which is right for ids the registry knows about but wrong as a
 * general prefix test — without the separator, a base id would also match any
 * future widget whose name merely starts with the same letters. Written out
 * here rather than importing extractBaseId (which reaches the registry) or
 * adding a third copy of the split — there are already two, in
 * templates/applyTemplate.ts and steps/StepDashboard.tsx.
 */
function isInstanceOf(gridId: string, baseId: string): boolean {
  return gridId === baseId || gridId.startsWith(`${baseId}-`);
}

function hasNoFilter(data: FilterDataLike | undefined): boolean {
  const filter = data?.filter;
  if (!filter) return true;
  return Object.values(filter).every((values) => !values?.length);
}

/**
 * The grid id to ring, or null when this tab has no instance of the widget.
 *
 * Null is a normal outcome, not an error — the user may be standing on a tab
 * that has no headlines panel. The rail keeps reading in that case; only the
 * ring goes away.
 */
export function resolveRailTarget(
  tab: TabLike | undefined,
  spec: RailTargetSpec | undefined
): string | null {
  if (!tab || !spec) return null;

  const candidates = tab.enabledWidgets.filter((id) =>
    isInstanceOf(id, spec.baseId)
  );
  if (candidates.length === 0) return null;
  if (spec.prefer !== "unfiltered") return candidates[0];

  // Ring the instance still carrying everything. Falls back to the first
  // candidate rather than null: a ring on the narrowed panel beats no ring.
  return (
    candidates.find((id) => hasNoFilter(tab.widgetFilters?.[id])) ??
    candidates[0]
  );
}

function selectionCount(data: FilterDataLike | undefined): number {
  if (!data) return 0;
  const sections = [
    ...Object.values(data.filter ?? {}),
    ...Object.values(data.highlight ?? {}),
  ];
  return sections.reduce((total, values) => total + (values?.length ?? 0), 0);
}

/**
 * Did this widget's filter/highlight selection just GAIN a value?
 *
 * "Any widgetFilters write" is too loose a test — opening a panel, clearing a
 * selection or any incidental write satisfies it, which is how a run can reach
 * the popout step without a watchlist ever being applied. That exact bug is
 * recorded in the demo guide's useStepDetection; it solves it by matching the
 * one hardcoded DEMO_WATCHLIST_ID, which a subscriber does not have. Counting
 * the selection instead generalises it: any real selection on the right widget
 * counts, and clearing one never does.
 *
 * `search` and `table` are deliberately excluded from the count — both change
 * on actions that have nothing to do with applying a watchlist.
 */
export function gainedSelection(
  next: FilterDataLike | undefined,
  prev: FilterDataLike | undefined
): boolean {
  return selectionCount(next) > selectionCount(prev);
}
